import moment from 'moment';
import { createTeamsCallLink, createTeamsChatLink } from './linkHelpers';
import { InterruptionDirection, InterruptionDto, InterruptionEmergencyCardView } from '../../types';
import { EMERGENCY_TTL_MS } from '../../constants';

export const parseBackendUtcMoment = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = moment.utc(value);

  return parsed.isValid() ? parsed : null;
};

export const formatEmergencyTtl = (milliseconds: number) => {
  if (milliseconds <= 0) {
    return '00:00';
  }

  const totalSeconds = Math.floor(Math.max(milliseconds, 0) / 1000);
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');

  return `${minutes}:${seconds}`;
};

const parseTtl = (ttl?: string | null) => {
  const match = ttl ? /^(\d+)m$/i.exec(ttl) : null;
  const minutes = match ? Number(match[1]) : 10;

  return Number.isFinite(minutes) && minutes > 0 ? minutes * 60 * 1000 : EMERGENCY_TTL_MS;
};

export const getEmergencyExpiresAtUtc = (interruption: InterruptionDto) => {
  const createdAt = parseBackendUtcMoment(interruption.createdAt);
  const expiresAt = parseBackendUtcMoment(interruption.expiresAt);
  const ttlMs = parseTtl(interruption.settings?.TTL);

  return expiresAt ?? (createdAt ?? moment.utc()).clone().add(ttlMs, 'milliseconds');
};

export const isEmergencyInterruptionExpired = (interruption: InterruptionDto, now = moment.utc()) =>
  !getEmergencyExpiresAtUtc(interruption).isAfter(now);

export const toInterruptionEmergencyCardView = (
  interruption: InterruptionDto,
  currentUserId: string,
): InterruptionEmergencyCardView => {
  const direction =
    interruption.creatorId === currentUserId ? InterruptionDirection.Outgoing : InterruptionDirection.Incoming;
  const isOutgoing = direction === InterruptionDirection.Outgoing;
  const creatorEmail = interruption.context?.creatorEmail ?? '';
  const creatorName = interruption.context?.creatorName ?? '';
  const recipientEmail = interruption.context?.recipientEmail ?? '';
  const recipientName = interruption.context?.recipientName ?? '';
  const targetUserEmail = isOutgoing ? recipientEmail : creatorEmail;
  const targetUserId = isOutgoing ? interruption.employeeId : interruption.creatorId;
  const targetUserName = isOutgoing ? recipientName || recipientEmail : creatorName || creatorEmail;
  const normalizedMessage = interruption.body?.trim() ?? '';
  const expiresAtUtc = getEmergencyExpiresAtUtc(interruption);

  return {
    id: interruption.id,
    chatLink: createTeamsChatLink(targetUserEmail),
    creatorEmail,
    direction,
    expiresAtIso: expiresAtUtc.toISOString(),
    message: normalizedMessage || 'Please call back as soon as possible.',
    recipientEmail,
    targetUserId,
    targetUserName: targetUserName || 'Unknown user',
    teamsCallLink: createTeamsCallLink(targetUserEmail),
    type: interruption.type,
  };
};
